import { useStore } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { createStore } from 'zustand/vanilla'

interface ClothingService {
  serviceType: string
  participantId: string
  isEditing: boolean
}

interface ClothingServiceState {
  clothingServices: Record<string, ClothingService>
  editingClothingServicesList: string[]
}

interface ClothingServiceStoreActions {
  startEditingService: (clothingState: ClothingService) => void
  endEditingService: (clothingServiceId: string) => void
}

type ClothingServiceStore = ClothingServiceState & ClothingServiceStoreActions

export function createClothingServiceStore(initState: ClothingServiceState) {
  return createStore<ClothingServiceStore>()(
    immer(set => ({
      ...initState,
      startEditingService(clothingState) {
        set(({ clothingServices, editingClothingServicesList }) => {
          const serviceId = `${clothingState.serviceType}-${clothingState.participantId}`
          clothingServices[serviceId] = clothingState
          editingClothingServicesList.push(serviceId)
        })
      },
      endEditingService(clothingServiceId) {
        set((state) => {
          state.editingClothingServicesList = state.editingClothingServicesList.filter(
            id => id !== clothingServiceId,
          )
        })
      },
    })),
  )
}

export function clothingServiceIsBeingEdited(clothingServiceId: string) {
  return ({ editingClothingServicesList }: ClothingServiceState) => {
    return editingClothingServicesList.includes(clothingServiceId)
  }
}

export const clothingServiceStore = createClothingServiceStore({
  clothingServices: {},
  editingClothingServicesList: [],
})

type StoreSelector<T, S> = (state: S) => T
interface UseBoundStore<S> {
  (): S
  <T>(selector: StoreSelector<T, S>): T
}

export const useClothingServiceStore: UseBoundStore<ClothingServiceStore> = <T>(selector?: StoreSelector<T, ClothingServiceStore>) => {
  const store = useStore(clothingServiceStore)

  if (!selector) {
    return store
  }

  return selector(store)
}
